<template>
  <el-dialog
    title="QR-код для переходу на сайт"
    :visible.sync="dialogVisible"
    width="480px"
    center
    append-to-body
    @close="handleClose"
  >
    <div class="qr-modal-content">
      <!-- Color Picker Option -->
      <div class="d-flex align-items-center justify-content-center mb-3">
        <span class="mr-2 small font-weight-bold">Колір фону картки:</span>
        <el-color-picker v-model="currentColor" size="small"></el-color-picker>
      </div>

      <!-- QR Card element for preview & download -->
      <div id="partnerQrCard" class="qr-card" :style="{ backgroundColor: currentColor }">
        <div class="qr-logo-wrap">
          <img :src="logoUrl || defaultLogo" alt="Logo" class="qr-logo" crossorigin="anonymous" />
        </div>
        <div class="qr-code-box">
          <img :src="qrCodeUrl" alt="QR Code" class="qr-code-img" crossorigin="anonymous" />
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="qr-actions mt-4">
        <div class="d-flex justify-content-center gap-2 mb-3">
          <el-button type="primary" icon="el-icon-download" @click="downloadQrCard" :loading="isDownloading">
            Завантажити QR-код
          </el-button>
          <el-popover placement="bottom" width="220" trigger="click">
            <div class="social-share-list">
              <div class="social-share-item" @click="share('viber')">
                <i class="fab fa-viber text-purple mr-2"></i> Viber
              </div>
              <div class="social-share-item" @click="share('telegram')">
                <i class="fab fa-telegram text-info mr-2"></i> Telegram
              </div>
              <div class="social-share-item" @click="share('whatsapp')">
                <i class="fab fa-whatsapp text-success mr-2"></i> WhatsApp
              </div>
              <div class="social-share-item" @click="share('facebook')">
                <i class="fab fa-facebook text-primary mr-2"></i> Facebook
              </div>
              <div class="social-share-item" @click="share('linkedin')">
                <i class="fab fa-linkedin text-info mr-2"></i> LinkedIn
              </div>
            </div>
            <el-button slot="reference" type="success" icon="el-icon-share">
              Поділитись
            </el-button>
          </el-popover>
        </div>
        <div class="text-center text-muted small">
          <span class="text-truncate d-inline-block" style="max-width: 380px;">{{ link }}</span>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import html2canvas from 'html2canvas';

export default {
  name: 'PartnerQrModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    link: {
      type: String,
      default: ''
    },
    logoUrl: {
      type: String,
      default: ''
    },
    initialBgColor: {
      type: String,
      default: '#FFAD3D'
    }
  },
  data() {
    return {
      dialogVisible: false,
      currentColor: '#FFAD3D',
      defaultLogo: '/images/ukasko-logo.svg',
      isDownloading: false
    };
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
    },
    initialBgColor: {
      immediate: true,
      handler(val) {
        if (val) this.currentColor = val;
      }
    }
  },
  computed: {
    qrCodeUrl() {
      if (!this.link) return '';
      return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(this.link)}`;
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:visible', false);
    },
    async downloadQrCard() {
      this.isDownloading = true;
      try {
        const card = document.getElementById('partnerQrCard');
        if (card) {
          const canvas = await html2canvas(card, {
            useCORS: true,
            allowTaint: true,
            scale: 2,
            backgroundColor: null
          });
          const image = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = image;
          link.download = 'partner-qr-code.png';
          link.click();
        }
      } catch (err) {
        console.error('Failed to generate QR image:', err);
        // Fallback: завантаження самого QR коду
        if (this.qrCodeUrl) {
          const link = document.createElement('a');
          link.href = this.qrCodeUrl;
          link.target = '_blank';
          link.download = 'qr-code.png';
          link.click();
        } else {
          this.$message.error('Не вдалося завантажити зображення');
        }
      } finally {
        this.isDownloading = false;
      }
    },
    share(platform) {
      const url = encodeURIComponent(this.link);
      const title = encodeURIComponent('Страхування онлайн');
      let shareUrl = '';

      switch (platform) {
        case 'viber':
          shareUrl = `viber://forward?text=${url}`;
          break;
        case 'telegram':
          shareUrl = `https://t.me/share/url?url=${url}&text=${title}`;
          break;
        case 'whatsapp':
          shareUrl = `https://api.whatsapp.com/send?text=${url}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
          break;
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
          break;
      }

      if (shareUrl) {
        window.open(shareUrl, '_blank');
      }
    }
  }
};
</script>

<style scoped lang="scss">
.qr-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-card {
  width: 320px;
  padding: 30px 20px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: background-color 0.3s ease;
}

.qr-logo-wrap {
  margin-bottom: 20px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-logo {
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
}

.qr-code-box {
  background: #ffffff;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.qr-code-img {
  width: 200px;
  height: 200px;
  display: block;
}

.social-share-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.social-share-item {
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  transition: background 0.2s;

  &:hover {
    background-color: #f0f2f5;
  }
}
</style>
